import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo, useState } from 'react';
import { ScimIntegrationDto } from 'src/api/FiretrolApiClient';
import { useScimIntegrations } from 'src/api/queries/admin/useScimIntegrations';
import {
  useScimIntegrationFullSync,
  useTestScimIntegration,
} from 'src/api/queries/admin/useScimIntegrationMutations';
import { showErrorToast, showSuccessToast, formatDateTime } from 'src/utils';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { ScimIntegrationDialog } from './components/ScimIntegrationDialog';
import { ScimMappingsDrawer } from './components/ScimMappingsDrawer';
import { ScimLogsDrawer } from './components/ScimLogsDrawer';
import {
  IconPlugConnected,
  IconRefresh,
  IconListDetails,
} from '@tabler/icons-react';

const getColumns = (): MRT_ColumnDef<ScimIntegrationDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'apiConnectionName',
      header: 'API Connection',
    },
    {
      accessorKey: 'scimPath',
      header: 'SCIM Path',
      size: 120,
    },
    {
      accessorKey: 'isEnabled',
      header: 'Enabled',
      size: 100,
      Cell: ({ cell }) => (
        <Badge color={cell.getValue<boolean>() ? 'green' : 'gray'}>
          {cell.getValue<boolean>() ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      accessorKey: 'lastSyncedAt',
      header: 'Last Sync',
      Cell: ({ cell }) => {
        const val = cell.getValue<Date | null>();
        return val ? formatDateTime(val) : 'Never';
      },
    },
    {
      accessorKey: 'userMappingCount',
      header: 'Users',
      size: 80,
    },
  ];
};

export const ScimIntegrations: React.FC = () => {
  const integrationsQuery = useScimIntegrations();
  const integrations = useMemo(
    () => integrationsQuery.data ?? [],
    [integrationsQuery.data],
  );
  const testIntegration = useTestScimIntegration();
  const fullSync = useScimIntegrationFullSync();
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    integration: ScimIntegrationDto | null;
  }>({ isOpen: false, integration: null });
  const [mappingsIntegration, setMappingsIntegration] =
    useState<ScimIntegrationDto | null>(null);
  const [logsIntegration, setLogsIntegration] =
    useState<ScimIntegrationDto | null>(null);

  const handleTestConnection = (integration: ScimIntegrationDto) =>
    testIntegration.mutate(integration.id, {
      onSuccess: (result) =>
        result.success
          ? showSuccessToast(`Connection to "${integration.name}" successful!`)
          : showErrorToast(`Connection failed: ${result.error}`),
    });

  const handleFullSync = (integration: ScimIntegrationDto) =>
    fullSync.mutate(integration.id, {
      onSuccess: () =>
        showSuccessToast(`Full sync queued for "${integration.name}"`),
    });

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'scim-integrations',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    onRefresh: integrationsQuery.refetch,
    tableOptions: {
      enableColumnFilters: false,
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 220 } },
      renderRowActions: ({ row }) => {
        const integration = row.original;
        return (
          <Group gap="xs">
            <EditActionIcon
              onClick={() => setDialogState({ isOpen: true, integration })}
            />
            <Tooltip label="Test Connection">
              <BasicActionIcon
                variant="subtle"
                color="blue"
                onClick={() => handleTestConnection(integration)}
              >
                <IconPlugConnected size={18} />
              </BasicActionIcon>
            </Tooltip>
            <Tooltip label="Full Sync">
              <BasicActionIcon
                variant="subtle"
                color="teal"
                onClick={() => handleFullSync(integration)}
              >
                <IconRefresh size={18} />
              </BasicActionIcon>
            </Tooltip>
            <Tooltip label="User Mappings">
              <span>
                <ViewActionIcon
                  onClick={() => setMappingsIntegration(integration)}
                />
              </span>
            </Tooltip>
            <Tooltip label="View Logs">
              <BasicActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setLogsIntegration(integration)}
              >
                <IconListDetails size={18} />
              </BasicActionIcon>
            </Tooltip>
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: integrations,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton
            onClick={() => setDialogState({ isOpen: true, integration: null })}
          >
            Create Integration
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => row.id,
    totalCount: integrations.length,
    isLoading: integrationsQuery.isLoading,
    isFetching: integrationsQuery.isFetching,
    isError: integrationsQuery.isError,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'SCIM Integrations' }],
        }}
        title="SCIM Integrations"
        helpText="Configure SCIM 2.0 integrations for automated user provisioning to connected applications"
      />
      <ScimIntegrationDialog
        open={dialogState.isOpen}
        onClose={() => setDialogState({ isOpen: false, integration: null })}
        onSave={() => setDialogState({ isOpen: false, integration: null })}
        integration={dialogState.integration}
      />
      <ScimMappingsDrawer
        integration={mappingsIntegration}
        onClose={() => setMappingsIntegration(null)}
      />
      <ScimLogsDrawer
        integration={logsIntegration}
        onClose={() => setLogsIntegration(null)}
      />
      {table}
    </>
  );
};
